/**
 * Hook: useTenantDashboardStatsAdmin
 * Descrição: Versão admin dos hooks de estatísticas que recebe tenantId como parâmetro
 * 
 * Replica a mesma lógica de useTenantDashboardStats mas para uso admin
 * 
 * @author Sistema
 * @update 2026-01-19 - Criação para Admin Tenant Management
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

// Interfaces reutilizadas
interface DashboardStats {
  conversations: {
    pending: number;
    open: number;
    closed: number;
    total: number;
  };
  messages: {
    sent: number;
    received: number;
    total: number;
  };
  agentCount: number;
  contactCount: number;
}

interface MessagesByDay {
  date: string;
  sent: number;
  received: number;
}

interface TimeSeriesData {
  messagesByDay: MessagesByDay[];
}

/**
 * Hook de estatísticas gerais para admin
 */
export function useTenantDashboardStatsAdmin(tenantId: string | undefined) {
  return useQuery({
    queryKey: ["admin-tenant-dashboard-stats", tenantId],
    queryFn: async (): Promise<DashboardStats> => {
      if (!tenantId) {
        return {
          conversations: { pending: 0, open: 0, closed: 0, total: 0 },
          messages: { sent: 0, received: 0, total: 0 },
          agentCount: 0,
          contactCount: 0,
        };
      }

      logger.debug("useTenantDashboardStatsAdmin", "Buscando stats do tenant", { tenantId });

      // Buscar estatísticas de conversas via RPC
      const { data: convStats, error: convError } = await supabase.rpc(
        "get_tenant_conversation_stats",
        { p_tenant_id: tenantId }
      );

      if (convError) {
        logger.error("useTenantDashboardStatsAdmin", "Erro nas stats de conversas", { convError });
      }

      // Buscar estatísticas de mensagens via RPC
      const { data: msgStats, error: msgError } = await supabase.rpc(
        "get_tenant_message_stats",
        { p_tenant_id: tenantId }
      );

      if (msgError) {
        logger.error("useTenantDashboardStatsAdmin", "Erro nas stats de mensagens", { msgError });
      }

      // Contar agentes
      const { count: agentCount } = await supabase
        .from("tenant_members")
        .select("*", { count: "exact", head: true })
        .eq("tenant_id", tenantId)
        .eq("is_active", true);

      // Contar contatos
      const { count: contactCount } = await supabase
        .from("contacts")
        .select("*", { count: "exact", head: true })
        .eq("tenant_id", tenantId);

      // RPC retorna com sufixo _count nas colunas
      const convRow = convStats?.[0] as Record<string, unknown> | undefined;
      const msgRow = msgStats?.[0] as Record<string, unknown> | undefined;

      return {
        conversations: {
          pending: Number(convRow?.pending_count ?? convRow?.pending ?? 0),
          open: Number(convRow?.open_count ?? convRow?.open ?? 0),
          closed: Number(convRow?.closed_count ?? convRow?.closed ?? 0),
          total: Number(convRow?.total_count ?? convRow?.total ?? 0),
        },
        messages: {
          sent: Number(msgRow?.sent_count ?? msgRow?.sent ?? 0),
          received: Number(msgRow?.received_count ?? msgRow?.received ?? 0),
          total: Number(msgRow?.total_count ?? msgRow?.total ?? 0),
        },
        agentCount: agentCount || 0,
        contactCount: contactCount || 0,
      };
    },
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook de time series para admin
 */
export function useTenantDashboardTimeSeriesAdmin(
  tenantId: string | undefined,
  days: number = 7
) {
  return useQuery({
    queryKey: ["admin-tenant-dashboard-timeseries", tenantId, days],
    queryFn: async (): Promise<TimeSeriesData> => {
      if (!tenantId) {
        return { messagesByDay: [] };
      }

      logger.debug("useTenantDashboardTimeSeriesAdmin", "Buscando timeseries", { tenantId, days });

      // Estratégia resiliente: tentar assinatura days_ago primeiro, fallback para datas
      let messagesByDay: any[] | null = null;
      let finalError: any = null;

      // Tentativa 1: usar parâmetro days_ago (assinatura preferida)
      const { data: daysAgoData, error: daysAgoError } = await supabase.rpc(
        "get_tenant_messages_by_day",
        {
          p_tenant_id: tenantId,
          days_ago: days,
        }
      );

      if (!daysAgoError) {
        // Sucesso com days_ago
        messagesByDay = daysAgoData;
        logger.debug("useTenantDashboardTimeSeriesAdmin", "Sucesso com days_ago", { 
          tenantId, 
          days,
          rawDataSample: JSON.stringify(daysAgoData?.slice(0, 2)),
          firstRowKeys: daysAgoData?.[0] ? Object.keys(daysAgoData[0]) : []
        });
      } else if (daysAgoError.code === "PGRST202") {
        // Erro de assinatura não encontrada - tentar fallback com start/end dates
        logger.warn("useTenantDashboardTimeSeriesAdmin", "Fallback para start/end dates", { 
          tenantId, 
          days, 
          originalError: daysAgoError.message 
        });

        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - days);

        const { data: datesData, error: datesError } = await supabase.rpc(
          "get_tenant_messages_by_day",
          {
            p_tenant_id: tenantId,
            p_start_date: startDate.toISOString(),
            p_end_date: endDate.toISOString(),
          }
        );

        if (!datesError) {
          messagesByDay = datesData;
          logger.debug("useTenantDashboardTimeSeriesAdmin", "Sucesso com start/end dates", { tenantId });
        } else {
          finalError = datesError;
          logger.error("useTenantDashboardTimeSeriesAdmin", "Ambas assinaturas falharam", { 
            tenantId, 
            daysAgoError: daysAgoError.message,
            datesError: datesError.message 
          });
        }
      } else {
        // Outro erro que não é de assinatura
        finalError = daysAgoError;
        logger.error("useTenantDashboardTimeSeriesAdmin", "Erro no timeseries", { 
          tenantId, 
          error: daysAgoError 
        });
      }

      // Mapear campos retornados pela RPC - com fallback para diferentes formatos de retorno
      return {
        messagesByDay: (messagesByDay || []).map((row: any) => {
          // Log para debug - remover após validação
          logger.debug("useTenantDashboardTimeSeriesAdmin", "Mapeando row", { row });
          
          return {
            // Suporte para date vindo como string ISO ou objeto Date
            date: typeof row.day_date === 'string' 
              ? row.day_date 
              : row.day_date?.toISOString?.()?.split('T')[0] || row.day_date,
            // Converter para número com fallback para diferentes nomes de campo
            sent: Number(row.sent_count ?? row.sentCount ?? row.sent ?? 0),
            received: Number(row.received_count ?? row.receivedCount ?? row.received ?? 0),
          };
        }),
      };
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
}
